import { DatePickerFr } from '../ui/DatePickerFr';
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { CheckCircle, CreditCard, Loader2, Repeat, Wallet } from 'lucide-react';
import { formatMoney } from './constants';
import { API } from './constants';
import { PaymentLinkSection } from '../PaymentLinkSection';

export default function ModalsPayment({
  getAmount, getAuthHeader, getName, handlePayment, paymentForm, processing,
  selectedDoc, setPaymentForm, setProcessing, setShowPaymentModal, showPaymentModal,
}) {
  return (
    <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-green-600" />
            Enregistrer un paiement
          </DialogTitle>
          <DialogDescription>
            {selectedDoc?.reference} - {getName(selectedDoc)}
            <div className="mt-1 text-sm font-medium text-indigo-600">
              Montant total : {formatMoney(getAmount(selectedDoc))}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Type de paiement */}
          <div className="bg-gray-50 p-3 rounded-lg space-y-3">
            <Label className="text-sm font-semibold text-gray-700">Type de paiement</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={paymentForm.paymentType === 'acompte' ? 'default' : 'outline'}
                className={`h-auto py-3 flex flex-col items-center gap-1 ${paymentForm.paymentType === 'acompte' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                onClick={() => setPaymentForm({ ...paymentForm, paymentType: 'acompte', amount: '' })}
              >
                <Wallet className="h-5 w-5" />
                <span className="text-sm font-medium">Acompte</span>
                <span className="text-[10px] opacity-80">Paiement partiel</span>
              </Button>
              <Button
                type="button"
                variant={paymentForm.paymentType === 'total' ? 'default' : 'outline'}
                className={`h-auto py-3 flex flex-col items-center gap-1 ${paymentForm.paymentType === 'total' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                onClick={() => setPaymentForm({ ...paymentForm, paymentType: 'total', amount: '' })}
              >
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Solde total</span>
                <span className="text-[10px] opacity-80">Paiement complet</span>
              </Button>
            </div>
          </div>

          {/* Montant acompte */}
          {paymentForm.paymentType === 'acompte' && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg space-y-2">
              <Label className="text-sm font-semibold text-amber-800">Montant de l'acompte *</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={getAmount(selectedDoc)}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="Ex: 500.00"
                  className="text-lg font-mono"
                />
                <span className="text-amber-700 font-semibold">€</span>
              </div>
              <p className="text-xs text-amber-600">
                Reste à payer : {formatMoney(Math.max(0, getAmount(selectedDoc) - (parseFloat(paymentForm.amount) || 0)))}
              </p>
            </div>
          )}

          {/* Informations du paiement */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Référence du paiement</Label>
              <Input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                placeholder="Réf. virement..."
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Date du paiement</Label>
              <DatePickerFr
                value={paymentForm.date}
                onChange={(v) => setPaymentForm({ ...paymentForm, date: v })}
                className="text-sm w-full"
              />
            </div>
          </div>

          {/* Mode de paiement */}
          <div>
            <Label className="text-xs text-gray-500">Mode de paiement</Label>
            <Select value={paymentForm.mode} onValueChange={(v) => setPaymentForm({ ...paymentForm, mode: v })}>
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="virement">Virement bancaire</SelectItem>
                <SelectItem value="sepa_core">Prélèvement SEPA Core</SelectItem>
                <SelectItem value="sepa_b2b">Prélèvement SEPA B2B</SelectItem>
                <SelectItem value="carte">Carte bancaire</SelectItem>
                <SelectItem value="cheque">Chèque</SelectItem>
                <SelectItem value="especes">Espèces</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Champs SEPA additionnels */}
          {(paymentForm.mode === 'sepa_core' || paymentForm.mode === 'sepa_b2b') && (
            <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Repeat className="h-4 w-4 text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-800">
                  {paymentForm.mode === 'sepa_b2b' ? 'SEPA B2B (Entreprise)' : 'SEPA Core (Particulier)'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-indigo-600">Nom du titulaire *</Label>
                  <Input
                    data-testid="sepa-customer-name"
                    value={paymentForm.sepaCustomerName || ''}
                    onChange={(e) => setPaymentForm({ ...paymentForm, sepaCustomerName: e.target.value })}
                    placeholder="Nom complet"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-indigo-600">Email du client *</Label>
                  <Input
                    data-testid="sepa-customer-email"
                    type="email"
                    value={paymentForm.sepaCustomerEmail || ''}
                    onChange={(e) => setPaymentForm({ ...paymentForm, sepaCustomerEmail: e.target.value })}
                    placeholder="email@exemple.com"
                    className="text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-indigo-600">IBAN *</Label>
                <Input
                  data-testid="sepa-iban"
                  value={paymentForm.sepaIban || ''}
                  onChange={(e) => setPaymentForm({ ...paymentForm, sepaIban: e.target.value.toUpperCase() })}
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                  className="text-sm font-mono"
                />
              </div>
              <Button
                type="button"
                data-testid="sepa-stripe-btn"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                disabled={processing || !paymentForm.sepaCustomerName || !paymentForm.sepaCustomerEmail || !paymentForm.sepaIban}
                onClick={async () => {
                  if (!selectedDoc) return;
                  setProcessing(true);
                  try {
                    await axios.post(
                      `${API}/domium/stripe/setup-sepa-schedule`,
                      {
                        reference: selectedDoc.reference,
                        customer_email: paymentForm.sepaCustomerEmail,
                        customer_name: paymentForm.sepaCustomerName,
                        iban: paymentForm.sepaIban,
                        sepa_type: paymentForm.mode === 'sepa_b2b' ? 'b2b' : 'core'
                      },
                      { headers: getAuthHeader() }
                    );
                    toast.success(`Prélèvement ${paymentForm.mode === 'sepa_b2b' ? 'SEPA B2B' : 'SEPA Core'} configuré via Stripe`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Erreur configuration SEPA Stripe');
                  } finally {
                    setProcessing(false);
                  }
                }}
              >
                {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Repeat className="h-4 w-4 mr-2" />}
                Lancer le prélèvement SEPA via Stripe
              </Button>
              <p className="text-[10px] text-indigo-500">
                {paymentForm.mode === 'sepa_b2b'
                  ? 'SEPA B2B : Délai J-1, non remboursable — réservé aux entreprises avec mandat B2B signé.'
                  : 'SEPA Core : Délai J-14, remboursable 8 semaines — adapté aux particuliers et TPE.'}
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-xs text-gray-500">Notes / Observations</Label>
            <Input
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              placeholder="Informations complémentaires..."
              className="text-sm"
            />
          </div>

          {/* Lien de paiement Stripe */}
          <PaymentLinkSection
            reference={selectedDoc?.reference || ''}
            amount={paymentForm.paymentType === 'acompte' ? (parseFloat(paymentForm.amount) || 0) : (selectedDoc?.calcul?.contribution_ht || selectedDoc?.calcul?.contribution_ttc || selectedDoc?.paiement?.montant || 0)}
            customerEmail={selectedDoc?.identite?.email || selectedDoc?.contact_email || selectedDoc?.email || ''}
            customerName={selectedDoc?.identite?.nom || selectedDoc?.identite?.raison_sociale || selectedDoc?.identite?.prenom || ''}
            source="domium"
            description={`Contribution DOMIUM - ${selectedDoc?.reference || ''}`}
            authHeader={getAuthHeader()}
          />

          {/* Programmation future */}
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="scheduled"
                checked={paymentForm.isScheduled}
                onChange={(e) => setPaymentForm({ ...paymentForm, isScheduled: e.target.checked })}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <Label htmlFor="scheduled" className="text-sm text-blue-800 cursor-pointer">
                📅 Programmer ce paiement pour une date future
              </Label>
            </div>
            {paymentForm.isScheduled && (
              <p className="mt-2 text-xs text-blue-600">
                Le paiement sera enregistré comme "programmé" et apparaîtra dans le suivi avec la date indiquée ci-dessus.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
            Annuler
          </Button>
          <Button
            onClick={handlePayment}
            disabled={processing || (paymentForm.paymentType === 'acompte' && (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0))}
            className={paymentForm.paymentType === 'acompte' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
          >
            {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {paymentForm.isScheduled ? '📅 Programmer' : '✓ Enregistrer'} {paymentForm.paymentType === 'acompte' ? "l'acompte" : 'le paiement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
